import apiClient from "@services/api/apiClient";
import API from "@config/shared/api-paths";

/**
 * ActiveDirectory sign-in requests.
 *
 * Used when a tenant's `authenticationMode` is ActiveDirectory. Both calls
 * resolve with the same `{access, refresh}` pair as `/auth/access`, which the
 * caller (AuthContext) stores exactly like a Local login.
 *
 *  - fetchActiveDirectoryToken: the silent happy path. The backend identifies
 *    the caller via Windows Integrated Auth — its first response is a
 *    `401 WWW-Authenticate: Negotiate` challenge that the BROWSER answers on its
 *    own with the user's Windows credentials, so JS only ever sees the final
 *    result. That needs `credentials: "include"`, a CORS response with
 *    `Access-Control-Allow-Credentials: true` + the exact SPA origin, and the API
 *    host trusted for integrated auth (intranet zone / AuthServerAllowlist).
 *    Any failure (401, CORS, network) means "fall back to the AD form".
 *
 *  - requestActiveDirectoryAccess: the fallback — AD username (UPN, e.g.
 *    "user@ad.internal") + password.
 */

/**
 * Remembers an explicit sign-out, so the AD login page doesn't sign the user
 * straight back in (Windows would vouch for them again at once). Kept in
 * sessionStorage: it survives a reload of the tab and is cleared by the next
 * successful sign-in (AuthContext).
 */
const SIGNED_OUT_KEY = "signedOut";

export const markSignedOut = () => sessionStorage.setItem(SIGNED_OUT_KEY, "1");
export const isSignedOut = () => sessionStorage.getItem(SIGNED_OUT_KEY) === "1";
export const clearSignedOut = () => sessionStorage.removeItem(SIGNED_OUT_KEY);

export const fetchActiveDirectoryToken = async () => {
  const { data } = await apiClient.get(API.AUTH.AD_TOKEN, {
    credentials: "include",
    skipAuth: true,
  });
  return data;
};

export const requestActiveDirectoryAccess = async ({ username, password }) => {
  const { data } = await apiClient.post(
    API.AUTH.AD_ACCESS,
    { username, password },
    { skipAuth: true },
  );
  return data;
};
